import { ApiError } from "@/clients/core/ApiError";
import { APP_ROUTES } from "@/constants/app-routes";
import { DaySelector } from "@/features/meal-plan/components/day-selector";
import { SingleScrollMealList } from "@/features/meal-plan/components/meal-list/single-scroll-meal-list";
import { MyPlanAddGuide } from "@/features/meal-plan/components/my-plan-add-guide";
import { MyPlanErrorState } from "@/features/meal-plan/components/my-plan-error-state";
import { MyPlanPageContainer } from "@/features/meal-plan/components/my-plan-page-container";
import { PlanAdjustmentModalContainer } from "@/features/meal-plan/components/plan-adjustment-modal/plan-adjustment-modal-container";
import {
  getMyPlanDaySelectionProperties,
  MyPlanAnalyticsProvider,
  useMyPlanAnalytics,
} from "@/features/meal-plan/hooks/use-my-plan-analytics";
import { useOpenMealCoach } from "@/features/meal-plan/hooks/use-open-meal-coach";
import { useGetPlan } from "@/hooks/requests/plan/use-get-plan";
import { useGetUser } from "@/hooks/requests/user/use-get-user";
import { useSearchParams } from "@/hooks/router/use-search-params";
import { pendingFoodAddAtom } from "@/stores/plan-food-add.store";
import { findScrollableElement, scrollToTop } from "@/utils/scroll-to-top";
import { IonSkeletonText, IonSpinner, useIonRouter } from "@ionic/react";
import { format, isValid, parseISO } from "date-fns";
import { useAtomValue } from "jotai";
import { type ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { CaloriesProgress } from "../../components/calories-progress";
import {
  getDayMealItems,
  getPlanDayEntries,
  getPlanDayEntry,
  getPlanDayEntryByDateKey,
  getPlanSelectorDays,
} from "../../utils/plan-week-data";

/**
 * My Plan — chat-centric single-scroll redesign. Every meal time is always
 * visible; per-meal actions go through the NutriCoach sparkle button.
 */
export function MyPlanPage() {
  const navigation = useIonRouter();
  const { plan, isLoading, error, refetch } = useGetPlan();
  const { user } = useGetUser();
  const searchParams = useSearchParams();
  const pageRef = useRef<HTMLElement>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  const handleRetry = useCallback(async () => {
    setIsRetrying(true);
    try {
      await refetch();
    } finally {
      setIsRetrying(false);
    }
  }, [refetch]);

  const selectedDay = useMemo(() => {
    const searchParamsDay = searchParams.get("day") as string;
    if (searchParamsDay) {
      return searchParamsDay;
    }

    const dateParam = searchParams.get("date") as string | null;
    const entryByDate = getPlanDayEntryByDateKey(plan, dateParam);
    if (entryByDate) {
      return entryByDate.day;
    }

    return format(new Date(), "EEEE").toLowerCase();
  }, [plan, searchParams]);

  const selectedDateKey = useMemo(() => {
    const dateParam = searchParams.get("date") as string;
    if (dateParam) {
      return dateParam;
    }

    const todayKey = format(new Date(), "yyyy-MM-dd");
    const entries = getPlanDayEntries(plan);
    const todayEntry = entries.find((entry) => entry.dateKey === todayKey);
    if (todayEntry) {
      return todayEntry.dateKey;
    }

    return getPlanDayEntry(plan, selectedDay)?.dateKey ?? "";
  }, [plan, searchParams, selectedDay]);

  const selectedDate = useMemo(() => {
    if (!selectedDateKey) {
      return null;
    }
    const parsedDate = parseISO(selectedDateKey);
    return isValid(parsedDate) ? parsedDate : null;
  }, [selectedDateKey]);

  const days = useMemo(() => getPlanSelectorDays(plan), [plan]);
  const analytics = useMyPlanAnalytics({ selectedDay, selectedDateKey });
  const openMealCoach = useOpenMealCoach(analytics.trackInteraction);

  const dayItems = useMemo(
    () => getDayMealItems(plan, selectedDay, selectedDateKey),
    [plan, selectedDay, selectedDateKey],
  );
  const isAddingFood = useAtomValue(pendingFoodAddAtom) !== null;
  // The "Adding food..." card takes a slot in the list, so it counts toward
  // the space the add-guide hint needs below the cards.
  const visibleCardCount = dayItems.length + (isAddingFood ? 1 : 0);

  // FAB + empty-state pill open the coach in general (day-level) mode.
  const handleAddNext = useCallback(() => {
    openMealCoach({ day: selectedDay, targetDate: selectedDateKey });
  }, [openMealCoach, selectedDay, selectedDateKey]);

  const handleDaySelect = (day: string, dateKey: string) => {
    if (dateKey !== selectedDateKey) {
      analytics.trackInteraction("day_selected", getMyPlanDaySelectionProperties(day, dateKey));
    }

    const urlSearchParams = new URLSearchParams(searchParams);
    urlSearchParams.set("day", day);
    if (dateKey) {
      urlSearchParams.set("date", dateKey);
    } else {
      urlSearchParams.delete("date");
    }
    navigation.push(`/plans/my-plan?${urlSearchParams.toString()}`, "none");
    requestAnimationFrame(() => scrollToTop(findScrollableElement()));
  };

  let content: ReactNode;

  if (isLoading) {
    content = (
      <MyPlanPageContainer pageRef={pageRef} showHeader={false}>
        <div className="flex w-full flex-col gap-6 px-4 pb-24">
          <IonSkeletonText animated className="h-16 w-full rounded-2xl" />
          <IonSkeletonText animated className="h-20 w-full rounded-2xl" />
          <div className="space-y-3">
            {[...Array(8)].map((_, index) => (
              <IonSkeletonText key={index} animated className="h-20 w-full rounded-2xl" />
            ))}
          </div>
        </div>
      </MyPlanPageContainer>
    );
  } else if (error) {
    const isAuthError = error instanceof ApiError && error.status === 401;
    content = (
      <MyPlanPageContainer pageRef={pageRef} showHeader={false}>
        <div className="flex h-full w-full flex-col items-center justify-center gap-4 px-4 pb-24">
          {isAuthError ? (
            <IonSpinner name="crescent" className="h-10 w-10" />
          ) : (
            <MyPlanErrorState
              message={error?.message}
              isOnboardingCompleted={user?.isOnboardingCompleted}
              onRetry={handleRetry}
              isRetrying={isRetrying}
              onFinishOnboarding={() => navigation.push(APP_ROUTES.ONBOARDING_START, "none")}
            />
          )}
        </div>
      </MyPlanPageContainer>
    );
  } else {
    content = (
      <MyPlanPageContainer pageRef={pageRef} showHeader={false}>
        <div className="flex w-full flex-1 flex-col gap-0 overflow-x-visible overflow-y-hidden">
          <div className="px-4">
            <DaySelector
              weekDays={days ?? []}
              selectedDateKey={selectedDateKey ?? ""}
              onSelectDate={handleDaySelect}
              theme="v2"
            />
          </div>
          <CaloriesProgress
            day={selectedDay}
            selectedDateKey={selectedDateKey}
            plan={plan ?? undefined}
            variant="v2"
          />
          <SingleScrollMealList
            selectedDay={selectedDay}
            selectedDate={selectedDate}
            selectedDateKey={selectedDateKey}
            pageRef={pageRef}
            onAddNext={handleAddNext}
          />
        </div>
        {visibleCardCount >= 1 && visibleCardCount < 3 && <MyPlanAddGuide />}
        <PlanAdjustmentModalContainer />
      </MyPlanPageContainer>
    );
  }

  return <MyPlanAnalyticsProvider analytics={analytics}>{content}</MyPlanAnalyticsProvider>;
}
